import { withErrorBoundary } from "@sentry/nextjs";
import cn from "classnames";
import { gql } from "graphql-request";
import { useCallback, useState, VFC } from "react";
import styled from "styled-components";
import { Image, Markdown } from "components";
import { MinusIcon, PlusIcon } from "icons";
import { Hero6Content } from "types/graphql/generated";
import { HeroAccordion } from "./HeroAccordion";

interface Props {
  content: Hero6Content;
}

const Hero6: VFC<Props> = withErrorBoundary(
  ({
    content: {
      pageTitle,
      title,
      textBackground: {
        data: { textBackground },
      },
      hero6Card: { title: cardTitle, text, buttonLabel, closeButtonLabel, cardSupTitle },
    },
  }) => {
    const [descriptionIsOpen, setDescriptionIsOpen] = useState(false);

    const handleToggleDescription = useCallback(() => {
      setDescriptionIsOpen(!descriptionIsOpen);
    }, [descriptionIsOpen]);

    return (
      <StyledHero6>
        <div className="Hero6__background">
          <div className="Content">
            <div className="Hero6__topContainer">
              <h1 className="Font_18_26_700 Color_dusk70">{pageTitle}</h1>
              <h2 className="Hero6__title Font_64_72_600 lg:Font_48_54_600 md:Font_48_54_600 sm:Font_40_48_600">
                {title}
              </h2>
              <div className="Hero6__textBackground__container">
                <Image
                  {...textBackground}
                  fill
                  className="Hero6__textBackground"
                  blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                />
              </div>
            </div>
          </div>
        </div>
        <div className="Content">
          <div className="Hero6__card Color_eucalyptus5">
            <div className="Hero6__supTitleContainer">
              <h3 className="Hero6__supTitle Color_dawn40 Font_24_32_600">{cardSupTitle}</h3>
            </div>
            <div className=" Hero6__cardTitleContainer">
              <h2 className="Hero6__cardTitle Font_40_48_600 md:Font_32_40_600">{cardTitle}</h2>
              <HeroAccordion isOpen={descriptionIsOpen} className="Color_eucalyptus5 Font_24_32_450">
                <Markdown className="HeroAccordion__text Font_18_26_450">{text || ""}</Markdown>
              </HeroAccordion>
              <button className="Hero6__button Color_eucalyptus5 Font_24_32_600" onClick={handleToggleDescription}>
                {descriptionIsOpen ? closeButtonLabel : buttonLabel}
                <PlusIcon
                  width={16}
                  type="button"
                  className={cn("HeroAccordion__iconExpanded inline", {
                    HeroAccordion__icon_expanded: descriptionIsOpen,
                  })}
                />
                <MinusIcon
                  width={16}
                  type="button"
                  className={cn("HeroAccordion__iconExpanded inline", {
                    HeroAccordion__icon_expanded: !descriptionIsOpen,
                  })}
                />
              </button>
            </div>
          </div>
        </div>
      </StyledHero6>
    );
  },
  {}
);

const StyledHero6 = styled.section`
  .Hero6__background {
    padding-top: 96px;
    padding-bottom: 251px;
    background: url("/images/noise.png"),
      radial-gradient(
        75.07% 87.86% at -11% 120.62%,
        rgb(126 100 251 / 100%) 14.44%,
        rgb(255 205 133 / 100%) 59%,
        rgb(246 243 241 / 100%) 100%
      );
    background-blend-mode: overlay, normal, normal;
    background-repeat: no-repeat;
  }

  .Hero6__topContainer {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .Hero6__title {
    margin-top: 24px;
    text-align: center;
    max-width: 814px;
  }

  .Hero6__textBackground {
    &__container {
      position: relative;
      top: -30px;
      right: -196px;
      width: 500px;
      height: 84px;
    }
  }

  .Hero6__card {
    position: relative;
    display: flex;
    justify-content: space-between;
    margin-top: -211px;
    padding: 80px 65px;
    max-width: 1240px;
    background: ${({ theme }) => theme.colors.eucalyptus["80"]};
  }

  .Hero6__supTitleContainer {
    position: relative;

    ::after {
      content: "";
      background-color: ${({ theme }) => theme.colors.dawn["40"]};
      position: absolute;
      top: 16px;
      left: 104px;
      height: 2px;
      width: 139px;
      margin-left: 24px;
    }
  }

  .Hero6__cardTitleContainer {
    max-width: 642px;
    margin-left: 40px;
  }

  .Hero6__cardTitle {
    max-width: 567px;
  }

  .Hero6__button {
    margin-top: 24px;
    cursor: pointer;
  }

  .HeroAccordion__content {
    overflow: hidden;
  }

  .HeroAccordion__contentWrapper {
    transition: height 0.175s ease;
  }

  .HeroAccordion__text {
    padding-top: 24px;
  }

  .HeroAccordion__icon {
    fill: ${({ theme }) => theme.colors.fog["00"]};
    margin-left: 12px;
    flex-shrink: 0;
    width: 16px;
    transition: transform 0.3s;

    &_expanded {
      display: none;
    }
  }

  .HeroAccordion__iconExpanded {
    fill: ${({ theme }) => theme.colors.fog["00"]};
    margin-left: 12px;
    flex-shrink: 0;
    transition: transform 0.3s;

    &_expanded {
      display: none;
    }
  }

  @media (min-width: 1200px) {
    .Hero6__background {
      background-size: 100% 100%;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Hero6__card {
      flex-direction: column;
    }

    .Hero6__cardTitleContainer {
      margin-top: 40px;
      margin-left: unset;
      max-width: unset;
    }

    .Hero6__cardTitle {
      max-width: unset;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Hero6__background {
      padding-top: 40px;
    }

    .Hero6__title {
      max-width: 616px;
    }

    .Hero6__textBackground {
      &__container {
        top: -22px;
        right: -152px;
        width: 375px;
        height: 72px;
      }
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Hero6__textBackground {
      &__container {
        display: none;
      }
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    background: unset;

    .Hero6__background {
      padding-bottom: 64px;
    }

    .Content {
      padding: 0;
    }

    .Hero6__topContainer {
      padding-left: 24px;
      padding-right: 24px;
    }

    .Hero6__title {
      margin-top: 16px;
    }

    .Hero6__card {
      margin-top: unset;
      padding: 64px 24px;
    }

    .HeroAccordion__icon {
      margin-left: 24px;
    }
  }
`;

gql`
  fragment Hero6Content on ComponentHeroHero6 {
    pageTitle
    title
    textBackground {
      data {
        textBackground: attributes {
          src: hash
          alt: alternativeText
        }
      }
    }
    hero6Card {
      title
      text
      buttonLabel
      closeButtonLabel
      cardSupTitle
    }
  }
`;

export { Hero6 };
export type { Props as Hero6Props };
